import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';

import { LoadingButton } from '@mui/lab';
import {
  Box,
  Chip,
  alpha,
  Stack,
  Select,
  MenuItem,
  Checkbox,
  TextField,
  InputLabel,
  Typography,
  FormControl,
  ListItemText,
  Autocomplete,
} from '@mui/material';

import useResponsiveScreen from 'src/hooks/useResponsiveScreen';

import { fetchRoles } from 'src/services/role.service';

import Iconify from 'src/components/iconify';

import { scrollbarStyle } from 'src/sections/style';

import countries from '../../../locale/phoneCode';
import { EMAIL_REGEX, PHONENUMBER_REGEX } from '../../../utils/regex';

AddUserForm.propTypes = {
  addUser: PropTypes.func,
  hideModal: PropTypes.func,
  currentRole: PropTypes.string,
  departmentsData: PropTypes.array,
};

export default function AddUserForm({
  addUser,
  hideModal,
  currentRole,
  departmentsData,
}) {
  const [selectedCountry, setSelectedCountry] = useState('+39');
  const [rolesList, setRolesList] = useState([]);
  const { t, i18n } = useTranslation();
  const { isSmallScreen } = useResponsiveScreen();

  const SELECT_ALL_VALUE = 'SELECT_ALL';

  useEffect(() => {
    const getRoles = async () => {
      try {
        const data = await fetchRoles();
        const filteredRoles = data.filter((role) =>
          role.active !== undefined ? role.active : role.isActive
        );
        setRolesList(filteredRoles);
      } catch (error) {
        console.error('Failed to fetch roles:', error);
      }
    };
    getRoles();
    countries.sort((a, b) => a[i18n.language].localeCompare(b[i18n.language]));
  }, [i18n.language]);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isValid, isDirty },
    reset,
  } = useForm({
    mode: 'all',
    defaultValues: {
      givenName: '',
      familyName: '',
      phoneNumber: '',
      departmentIds: [],
      role: '',
      email: '',
    },
  });

  const cleanRoleName = (roleName) => {
    if (!roleName) return '';
    return roleName
      .replace('ROLE_', '')
      .replace(/_/g, ' ')
      .trim()
      .toLowerCase()
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const onSubmit = (data) => {
    data.phoneNumber = `(${selectedCountry}) ${data.phoneNumber}`;
    addUser({ ...data, status: true, isVerified: true });
    hideModal();
    reset();
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={3} mt={2}>
        <TextField
          name="givenName"
          label={t('attributes.name')}
          error={!!errors.givenName}
          helperText={errors.givenName?.message || ''}
          {...register('givenName', {
            required: t('formControl.enterName'),
            minLength: { value: 3, message: t('formControl.minName') },
          })}
          size={isSmallScreen ? 'small' : 'medium'}
        />

        <TextField
          name="familyName"
          label={t('attributes.lastName')}
          error={!!errors.familyName}
          helperText={errors.familyName?.message || ''}
          {...register('familyName', {
            required: t('formControl.enterLastName'),
            minLength: { value: 3, message: t('formControl.minLastName') },
          })}
          size={isSmallScreen ? 'small' : 'medium'}
        />

        <Stack direction="row" spacing={1}>
          <FormControl sx={{ width: '30%' }}>
            <InputLabel id="country-code-label">{t('attributes.country')}</InputLabel>
            <Select
              labelId="country-code-label"
              label={t('attributes.country')}
              value={selectedCountry}
              onChange={(e) => setSelectedCountry(e.target.value)}
              renderValue={(val) => `(${val})`}
              size={isSmallScreen ? 'small' : 'medium'}
            >
              {countries.map((country, index) => (
                <MenuItem key={index} value={country.code}>
                  {`${country[i18n.language]} (${country.code})`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            type="number"
            label={t('attributes.phoneNumber')}
            error={!!errors.phoneNumber}
            helperText={errors.phoneNumber?.message || ''}
            {...register('phoneNumber', {
              required: t('formControl.enterPhoneNumber'),
              pattern: {
                value: PHONENUMBER_REGEX,
                message: t('formControl.invalidPhoneNumber'),
              },
            })}
            size={isSmallScreen ? 'small' : 'medium'}
          />
        </Stack>

        <Controller
          name="role"
          control={control}
          rules={{ required: t('formControl.selectRole') }}
          render={({ field }) => (
            <Autocomplete
              fullWidth
              options={rolesList}
              value={rolesList.find((r) => r.roleName === field.value) || null}
              getOptionLabel={(option) => cleanRoleName(option?.roleName || '')}
              isOptionEqualToValue={(option, value) => option.roleName === value.roleName}
              onChange={(_, newValue) =>
                field.onChange(newValue ? newValue.roleName : '')
              }
              onBlur={field.onBlur}
              size={isSmallScreen ? 'small' : 'medium'}
              noOptionsText={t('formControl.noRoleFound', 'No matching role')}
              ListboxProps={{ sx: scrollbarStyle }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('pages.usersPage.form.role')}
                  error={!!errors.role}
                  helperText={errors.role?.message}
                />
              )}
            />
          )}
        />

        <Controller
          name="departmentIds"
          control={control}
          rules={{
            validate: (v) => (v && v.length > 0) || 'At least one department is required',
          }}
          render={({ field }) => {
            const safeDepts = departmentsData || [];
            const currentValue = (field.value || []).map(Number);
            const isAllSelected =
              safeDepts.length > 0 && currentValue.length === safeDepts.length;
            const selectedOptions = safeDepts.filter((d) =>
              currentValue.includes(Number(d.id))
            );
            const options = [
              {
                id: SELECT_ALL_VALUE,
                name: t('buttons.selectAllDepartments', 'Select All Departments'),
              },
              ...safeDepts,
            ];

            return (
              <Autocomplete
                fullWidth
                multiple
                disableCloseOnSelect
                options={options}
                value={selectedOptions}
                getOptionLabel={(option) => option.name || ''}
                isOptionEqualToValue={(option, value) =>
                  Number(option.id) === Number(value.id)
                }
                filterOptions={(opts, state) => {
                  const input = state.inputValue.toLowerCase();
                  return opts.filter(
                    (o) =>
                      o.id === SELECT_ALL_VALUE || o.name.toLowerCase().includes(input)
                  );
                }}
                onChange={(event, newValue) => {
                  const clickedSelectAll = newValue.some(
                    (o) => o.id === SELECT_ALL_VALUE
                  );
                  if (clickedSelectAll) {
                    field.onChange(
                      isAllSelected ? [] : safeDepts.map((d) => Number(d.id))
                    );
                    return;
                  }
                  field.onChange(newValue.map((o) => Number(o.id)));
                }}
                componentsProps={{
                  paper: {
                    sx: {
                      mt: 0.5,
                      borderRadius: 2,
                      boxShadow: (theme) =>
                        theme.customShadows?.z20 || '0 8px 24px rgba(0,0,0,0.14)',
                    },
                  },
                }}
                renderOption={(props, option, { selected }) => {
                  const optionProps = props;
                  const isSelectAll = option.id === SELECT_ALL_VALUE;
                  const checked = isSelectAll ? isAllSelected : selected;
                  const indeterminate =
                    isSelectAll &&
                    currentValue.length > 0 &&
                    currentValue.length < safeDepts.length;

                  return (
                    <Box
                      component="li"
                      {...optionProps}
                      sx={{
                        px: 1.5,
                        py: 1,
                        borderRadius: 1,
                        mx: 0.5,
                        my: isSelectAll ? 0.5 : 0,
                        ...(isSelectAll && {
                          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                        }),
                        '&:hover': {
                          bgcolor: (theme) =>
                            alpha(theme.palette.primary.main, isSelectAll ? 0.14 : 0.06),
                        },
                      }}
                    >
                      <Checkbox
                        checked={checked}
                        indeterminate={indeterminate}
                        size="small"
                        sx={{ mr: 1 }}
                      />

                      <ListItemText
                        primary={option.name}
                        sx={{ flex: 1 }}
                        primaryTypographyProps={{
                          fontWeight: isSelectAll ? 700 : 500,
                          color: isSelectAll ? 'primary.main' : 'text.primary',
                          fontSize: '0.875rem',
                        }}
                      />
                      {isSelectAll && (
                        <Typography
                          variant="caption"
                          sx={{ fontWeight: 600, color: 'primary.main' }}
                        >
                          {currentValue.length}/{safeDepts.length}
                        </Typography>
                      )}
                    </Box>
                  );
                }}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      key={option.id}
                      label={option.name}
                      size="small"
                      onDelete={getTagProps({ index }).onDelete}
                      sx={{
                        borderRadius: 1,
                        fontWeight: 600,
                        m: 0.4,
                        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
                        color: 'primary.dark',
                        '& .MuiChip-deleteIcon': {
                          color: 'primary.main',
                          '&:hover': { color: 'primary.dark' },
                        },
                      }}
                    />
                  ))
                }
                limitTags={3}
                size={isSmallScreen ? 'small' : 'medium'}
                noOptionsText={
                  <Stack alignItems="center" spacing={0.5} sx={{ py: 1 }}>
                    <Iconify
                      icon="eva:search-outline"
                      width={20}
                      sx={{ color: 'text.disabled' }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      {t('formControl.noDepartmentFound', 'No matching department')}
                    </Typography>
                  </Stack>
                }
                ListboxProps={{ sx: { ...scrollbarStyle, py: 0.5 } }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('attributes.department')}
                    error={!!errors.departmentIds}
                    helperText={errors.departmentIds?.message}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <Iconify
                            icon="eva:search-outline"
                            width={18}
                            sx={{ color: 'text.disabled', ml: 0.5, mr: 0.5 }}
                          />
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                  />
                )}
              />
            );
          }}
        />

        <TextField
          label={t('pages.usersPage.form.emailAdress')}
          error={!!errors.email}
          helperText={errors.email?.message || ''}
          {...register('email', {
            required: t('formControl.enterEmail'),
            pattern: { value: EMAIL_REGEX, message: t('formControl.invalidEmail') },
          })}
          size={isSmallScreen ? 'small' : 'medium'}
        />
      </Stack>

      <Stack mt={3}>
        <LoadingButton
          fullWidth
          size="large"
          type="submit"
          variant="contained"
          disabled={!isValid || !isDirty}
        >
          {t('buttons.confirm')}
        </LoadingButton>
      </Stack>
    </form>
  );
}
